import React, { memo, type ReactElement } from 'react';
import { Box, TabContent } from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodSidePanelSummary,
  SodSidePanelUser,
} from '../../../../shared/components';
import SodSidePanelHistory from '../../../../shared/components/SodSidePanelHistory';
import { VIOLATIONS_DETAILS_TABS } from '../../../../shared/constants';
import { useTypedSelector } from '../../../../shared/hooks';
import DetailsFooter from '../DetailsFooter';

import ViolationsTabDetailsConflicts from './ViolationsTabDetailsConflicts';
import ViolationsTabDetailsFooter from './ViolationsTabDetailsFooter';
import ViolationsTabDetailsMitigating from './ViolationsTabDetailsMitigating';
import ViolationsTabDetailsSidePanel from './ViolationsTabDetailsSidePanel';

import './ViolationsTabDetails.css';

interface ViolationsTabDetailsProps {
  activeTab: string;
  handleChange: (value: string) => void;
  className?: string;
  handleBackButton: () => void;
  username: string;
}

/**
 * ViolationsTabDetails
 *
 * TODO:
 * - Move the TabContent into a separate files
 */
function ViolationsTabDetails({
  activeTab,
  handleChange,
  handleBackButton,
  username,
  className,
}: ViolationsTabDetailsProps): ReactElement {
  const {
    queryParam,
    sidepanel: { isSidePanelVisible },
    violationsTab: { isFooterVisible },
  } = useTypedSelector((state) => state.aag.details);

  const wrapperClasses = cx(
    'Sod-ViolationsTabDetails',
    isSidePanelVisible && 'Sod-ViolationsTabDetails--isSidePanelVisible',
    className
  );
  const contentClasses = cx(
    'Sod-ViolationsTabDetails-content',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    isFooterVisible && 'Sod-ViolationsTabDetails-content--isFooterVisible'
  );
  const asidesClasses = cx('Sod-ViolationsTabDetails-asides');

  const commonTabContentClasses = 'Sod-ViolationsTabDetails-tabContent';

  return (
    <Box tag='div' className={wrapperClasses}>
      <Box tag='div' className={contentClasses}>
        {/* Conflicts */}
        <ViolationsTabDetailsConflicts
          activeTab={activeTab}
          className={commonTabContentClasses}
        />
        {/* Mitigating Control - Only render when active tab is mitigating */}
        {activeTab === VIOLATIONS_DETAILS_TABS.TAB_MITIGATING && (
          <ViolationsTabDetailsMitigating
            sodKey={queryParam ?? ''}
            activeTab={activeTab}
            className={commonTabContentClasses}
          />
        )}
        {/* Summary */}
        <TabContent
          className='Sod-ViolationsTabDetails-tabContent'
          value={activeTab}
          tabValue={VIOLATIONS_DETAILS_TABS.TAB_SUMMARY}>
          <Box className='Sod-ViolationsTabDetails-tabContentWrapper'>
            {/* Update sodKey once available in redux store */}
            <SodSidePanelSummary
              sodKey={queryParam ?? ''}
              handleOnUserClick={() => {
                handleChange(VIOLATIONS_DETAILS_TABS.TAB_USER);
              }}
              className='Sod-SummaryDetails'
            />
          </Box>
        </TabContent>
        {/* User */}
        <TabContent
          className={commonTabContentClasses}
          value={activeTab}
          tabValue={VIOLATIONS_DETAILS_TABS.TAB_USER}>
          <Box className='Sod-ViolationsTabDetails-tabContentWrapper'>
            <SodSidePanelUser usernameQuery={username} />
          </Box>
        </TabContent>
        {/* History */}
        <TabContent
          className={commonTabContentClasses}
          value={activeTab}
          tabValue={VIOLATIONS_DETAILS_TABS.TAB_HISTORY}>
          <SodSidePanelHistory sodKey={queryParam ?? ''} isInDetailsPage />
        </TabContent>
        <DetailsFooter
          content={
            <ViolationsTabDetailsFooter handleBackButton={handleBackButton} />
          }
          isVisible={Boolean(isFooterVisible)}
        />
      </Box>
      <Box tag='div' className={asidesClasses}>
        <ViolationsTabDetailsSidePanel activeTab={activeTab} />
      </Box>
    </Box>
  );
}

ViolationsTabDetails.defaultProps = {
  className: null,
};

export default memo(ViolationsTabDetails);
